import React, { useEffect, useRef, useState } from "react";
import { FileText } from "lucide-react";
import { AttachPicker, uploadAttachments } from "../Attachments";
import { toast } from "sonner";
import api, { today, formatApiError } from "../../lib/api";
import { Autocomplete } from "../Autocomplete";
import { SignaturePad } from "../SignaturePad";
import { Dialog, DialogContent, DialogHeader, DialogTrigger, DialogFooter } from "../ui/dialog";
import { field, label, SplitRows, DocTitle, TypeChooser, downloadDocPdf, cleanSplits, splitsTotal } from "./shared";

const blank = () => ({ date: today(), receiver_name: "", mobile: "", voucher_type: "", employee_name: "", employee_id: "", account_name: "", post_to_ledger: false, reference: "", splits: [{ method: "cash", amount: "", reference: "" }] });

const EmployeePick = ({ form, setForm, employees }) => (
  <div>
    <label className={label}>Employee <span className="text-muted-foreground font-normal">(from Salary ledger)</span></label>
    <select data-testid="voucher-employee" className={field} value={form.employee_id}
      onChange={(e) => {
        const emp = employees.find((x) => x.id === e.target.value);
        setForm({ ...form, employee_id: e.target.value, employee_name: emp ? emp.name : "", receiver_name: emp ? emp.name : form.receiver_name, mobile: form.mobile || (emp ? (emp.phone || "") : "") });
      }}>
      <option value="">Select employee…</option>
      {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}{emp.emp_code ? ` (${emp.emp_code})` : ""}</option>)}
    </select>
    {employees.length === 0 && <p className="text-xs text-muted-foreground mt-1">No employees yet — add them in the Salary page first.</p>}
  </div>
);

const LedgerPost = ({ form, setForm }) => {
  const [accts, setAccts] = useState(null);
  const toggle = (on) => {
    setForm({ ...form, post_to_ledger: on, account_name: on ? "" : form.account_name });
    if (on && accts === null) api.get("/credit-accounts", { params: { include_all: true } }).then((r) => setAccts([...new Set((r.data || []).map((a) => a.account))])).catch(() => setAccts([]));
  };
  return (
    <div className="rounded-md border border-input p-3 space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
        <input data-testid="voucher-post-ledger" type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={!!form.post_to_ledger} onChange={(e) => toggle(e.target.checked)} />
        Post to credit ledger (paid to a credit account)
      </label>
      {form.post_to_ledger ? (
        <select data-testid="voucher-ledger-account" className={field} value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })}>
          <option value="">Select credit account…</option>
          {(accts || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      ) : (
        <>
          <label className={label}>Account name <span className="text-muted-foreground font-normal">(optional — not posted to the credit ledger)</span></label>
          <input data-testid="voucher-account-name" className={field} value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} placeholder="e.g. a supplier/account you pay regularly" />
        </>
      )}
      {form.post_to_ledger && <p className="text-xs text-muted-foreground">This payment will show as a debit in the selected account's credit ledger.</p>}
    </div>
  );
};

const CapitalToggle = ({ capMode, setCapMode, form, setForm, caps }) => (
  <div className="rounded-md border border-input p-3 bg-muted/30">
    <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
      <input type="checkbox" data-testid="voucher-capital-toggle" checked={capMode} onChange={(e) => { setCapMode(e.target.checked); setForm((f) => ({ ...f, receiver_name: "" })); }} />
      Partner capital withdrawal (drawings)
    </label>
    {capMode && (
      <>
        <select data-testid="voucher-capital-partner" className={field + " mt-2"} value={form.receiver_name} onChange={(e) => setForm({ ...form, receiver_name: e.target.value })}>
          <option value="">Select partner…</option>
          {caps.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <p className="text-xs text-muted-foreground mt-1">Money paid = <b>debit (taken)</b> from this partner's capital account.</p>
      </>
    )}
  </div>
);

const ReceiverFields = ({ form, setForm }) => (
  <div className="grid grid-cols-2 gap-3">
    <div>
      <label className={label}>Paid to (receiver)</label>
      <Autocomplete field="party" testid="voucher-receiver" value={form.receiver_name} onChange={(v) => setForm({ ...form, receiver_name: v })} placeholder="Receiver name" />
    </div>
    <div>
      <label className={label}>Mobile number</label>
      <input data-testid="voucher-mobile" className={field} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="10-digit" />
    </div>
  </div>
);

const useVoucherLookups = (open, setForm) => {
  const [preview, setPreview] = useState("VCH-…");
  const [types, setTypes] = useState([]);
  const [caps, setCaps] = useState([]);
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/vouchers/next-number", { params: { doc_type: "voucher" } }).then((r) => setPreview(r.data.preview)).catch((e) => console.error(e));
    api.get("/voucher-types").then((r) => { setTypes(r.data); setForm((f) => ({ ...f, voucher_type: f.voucher_type || r.data[0] || "" })); }).catch((e) => console.error(e));
    api.get("/capital-accounts").then((r) => setCaps(r.data || [])).catch(() => setCaps([]));
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => setEmployees([]));
  }, [open, setForm]);
  return { preview, types, setTypes, caps, employees };
};

export function VoucherDialog({ onDone, triggerClass, compact }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [files, setFiles] = useState([]);
  const [newType, setNewType] = useState("");
  const [capMode, setCapMode] = useState(false);
  const cashierRef = useRef(null);
  const receiverRef = useRef(null);
  const { preview, types, setTypes, caps, employees } = useVoucherLookups(open, setForm);

  const addType = async () => {
    if (!newType.trim()) return;
    const { data } = await api.post("/voucher-types", { name: newType.trim() });
    setTypes(data);
    setForm({ ...form, voucher_type: newType.trim() });
    setNewType("");
  };

  const save = async () => {
    if (!form.receiver_name) return toast.error("Receiver is required");
    if (!capMode && !form.mobile) return toast.error("Receiver and mobile are required");
    if (form.voucher_type === "Salary" && !form.employee_id) return toast.error("Select an employee for salary");
    if (form.post_to_ledger && !(form.account_name || "").trim()) return toast.error("Choose the credit account for the ledger");
    const splits = cleanSplits(form.splits);
    if (splits.length === 0) return toast.error("Enter at least one amount");
    if (splits.some((s) => s.method !== "cash" && !s.reference.trim())) return toast.error("Add a reference for non-cash methods");
    const cashier_signature = cashierRef.current?.getData() || "";
    const receiver_signature = receiverRef.current?.getData() || "";
    if (!capMode && (!cashier_signature || !receiver_signature)) return toast.error("Both cashier and receiver signatures are required");
    try {
      const { data } = await api.post("/vouchers", {
        date: form.date, receiver_name: form.receiver_name, mobile: form.mobile,
        voucher_type: form.voucher_type, employee_name: form.employee_name, employee_id: form.employee_id, account_name: form.account_name, post_to_ledger: !!form.post_to_ledger, reference: form.reference,
        amount: splitsTotal(splits), pay_method: splits[0].method, splits, cashier_signature, receiver_signature,
      });
      toast.success(`Voucher ${data.number} created`);
      if (files.length) await uploadAttachments("voucher", data.id, files);
      await downloadDocPdf(data.id, data.number);
      setOpen(false); setForm(blank()); setCapMode(false); setFiles([]);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-voucher-btn" className={triggerClass || "h-11 px-5 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"}>
          <FileText size={16} /> {compact ? "Voucher" : "New Voucher"}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DocTitle title="Payment Voucher" preview={preview} /></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className={label}>Voucher type</label>
            <TypeChooser items={types} value={form.voucher_type} onPick={(t) => setForm({ ...form, voucher_type: t })} testidOf={(t) => `vtype-${t}`} round
              newValue={newType} setNewValue={setNewType} onAdd={addType} addTestid="add-vtype-btn" newTestid="new-vtype" placeholder="Add a type…" />
          </div>
          {form.voucher_type === "Salary" && <EmployeePick form={form} setForm={setForm} employees={employees} />}
          {!capMode && <LedgerPost form={form} setForm={setForm} />}
          {caps.length > 0 && <CapitalToggle capMode={capMode} setCapMode={setCapMode} form={form} setForm={setForm} caps={caps} />}
          {!capMode && <ReceiverFields form={form} setForm={setForm} />}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Date</label>
              <input data-testid="voucher-date" type="date" className={field + " num"} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          <SplitRows rows={form.splits} setRows={(r) => setForm({ ...form, splits: r })} prefix="voucher" />
          <div>
            <label className={label}>Reference / note</label>
            <input data-testid="voucher-ref" className={field} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="optional" />
          </div>
          <AttachPicker files={files} setFiles={setFiles} testid="voucher-attach" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <SignaturePad ref={cashierRef} label="Cashier signature" testid="voucher-sign-cashier" />
            <SignaturePad ref={receiverRef} label="Receiver signature" testid="voucher-sign-receiver" />
          </div>
          <p className="text-xs text-muted-foreground">This voucher posts to the cash book as a payment out and downloads a signed PDF.</p>
        </div>
        <DialogFooter>
          <button data-testid="voucher-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Create &amp; download</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
